import { Card, Flex, SimpleTable, Skeleton, Typography } from "djuno-design";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;

const SkeletonPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Skeleton</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Skeleton component in Djuno Design is used to display a loading
            placeholder while content is being fetched or processed. It visually
            represents the layout and shape of the content, helping to enhance
            user experience during loading times.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          {/* <Text size="sm"></Text> */}
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Shape Variants
                </Text>
                : The Skeleton can take different shapes, such as rectangles,
                circles, or squares.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Size Options
                </Text>
                : It comes in three predefined sizes: small, medium, and large.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Loading Animation
                </Text>
                : The Skeleton can be animated with a pulsing effect or remain
                static.
              </Text>
            </li>
          </ul>

          <Title level={5} className="mt-8">
            Skeleton Variants
          </Title>

          <Title level={6} className="mt-2">
            1. Shape
          </Title>
          <Text size="sm">
            The Skeleton component can take on three shapes:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  rectangle
                </Text>
                : Default full-width rectangle.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  circle
                </Text>
                : Perfect circle, useful for avatars or profile pictures.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  square
                </Text>
                : A square shape with a flexible size.
              </Text>
            </li>
          </ul>

          <Title level={6} className="mt-4">
            2. Size
          </Title>
          <Text size="sm">
            The Skeleton can be customized with three predefined sizes:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  small
                </Text>
                : Small height for compact spaces.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  medium
                </Text>
                : The default size, suitable for most content placeholders.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  large
                </Text>
                : Taller, large placeholders.
              </Text>
            </li>
          </ul>

          <Title level={6} className="mt-4">
            3. Animation
          </Title>
          <Text size="sm">
            The Skeleton can be either static or animated with a pulsing effect:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  none
                </Text>
                : No animation; the Skeleton remains static.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  pulse
                </Text>
                : A smooth pulsing animation to indicate loading.
              </Text>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="w-full">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction="col" className="gap-4 w-full">
            <ExpandableCard
              codeViewerProps={{
                code: SkeletonExample1,
              }}
            >
              <Flex direction={"col"} className="gap-3 w-full">
                <Skeleton />
                <Skeleton uiSize="small" />
                <Skeleton shape="square" />
                <Skeleton shape="circle" />
              </Flex>
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* shape */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">shape</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Determines the shape of the Skeleton.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "ISkeleton.ts#L31"}
                        size="xs"
                        code
                      >
                        SkeletonShapes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">rectangle</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* size */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">uiSize</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Defines the height or size of the Skeleton.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "index.ts#L55"}
                        size="xs"
                        code
                      >
                        SizeTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">medium</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* animation */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">animation</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Sets the animation style for the Skeleton.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Alert.ts#L36"}
                        size="xs"
                        code
                      >
                        AlertTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">pulse</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* className */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">className</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        CSS classes for custom styling of the skeleton
                        component.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* style */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">style</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Inline styles for additional customization of the
                        skeleton's appearance.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        React.CSSProperties
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const SkeletonExample1 = `<Flex direction={"col"} className="gap-3 w-full">
  <Skeleton />
  <Skeleton size="small" />
  <Skeleton shape="square" />
  <Skeleton shape="circle" />
</Flex>`;
export default SkeletonPage;
